import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Mail, ArrowLeft, FileDown } from "lucide-react";
import { api, API, fileUrl, formatUSD } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import { VideoSlot } from "@/components/VideoSlot";
import { FitImage } from "@/components/FitImage";
import { useCart } from "@/context/CartContext";

export default function BusinessPage() {
  const [cols, setCols] = useState(null);
  useEffect(() => { api.get("/business/collections").then(r => setCols(r.data)).catch(() => setCols([])); }, []);
  return (
    <PublicShell>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-24" data-testid="business-page">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="overline text-mute mb-3">For Businesses</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink">Art for Your Space</h1>
            <p className="text-ink-soft mt-4 max-w-xl">Curated print sets for restaurants, hotels, offices and shared spaces — one flat price per collection, printed and shipped ready to hang.</p>
          </div>
          <a href={`${API}/business/catalog.pdf`} target="_blank" rel="noreferrer" data-testid="business-catalog-pdf"
            className="inline-flex items-center gap-2 px-5 py-3 border border-ink text-[11px] uppercase tracking-[0.18em] hover:bg-ink hover:text-cream transition-colors">
            <FileDown size={14}/> Download PDF Catalog
          </a>
        </div>
        <VideoSlot page="business" spot="top" className="mb-14" />
        {cols === null ? <p className="text-mute">Loading…</p> : cols.length === 0 ? (
          <p className="text-mute" data-testid="business-empty">Collections coming soon. <Link to="/contact" className="underline">Contact us</Link> for a custom set.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="business-grid">
            {cols.map(c => (
              <Link key={c.id} to={`/business/${c.id}`} className="group block" data-testid={`business-card-${c.id}`}>
                <FitImage src={fileUrl(c.cover_image_url)} alt={c.name} className="relative aspect-[4/3] bg-cream-soft" sizes="(min-width:1024px) 30vw, 100vw" imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
                <div className="flex items-start justify-between gap-4 mt-4">
                  <div>
                    <h3 className="font-serif text-2xl text-ink">{c.name}</h3>
                    <p className="text-sm text-ink-soft mt-1 line-clamp-2">{c.description}</p>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-mute mt-2">{c.artworks.length} pieces · {c.print_size} in</div>
                  </div>
                  <div className="font-mono-brand text-lg text-ink whitespace-nowrap" data-testid={`business-price-${c.id}`}>{formatUSD(c.price_usd)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
        <VideoSlot page="business" spot="bottom" className="mt-20" />
      </section>
    </PublicShell>
  );
}

const FIELDS = [
  ["business_name", "Business name", true], ["contact_name", "Your name", true], ["email", "Email", true], ["phone", "Phone", false],
  ["space_type", "Type of space (restaurant, hotel, office…)", false], ["quantity", "Quantity / number of locations", false],
  ["sizing", "Preferred sizes", false], ["framing", "Framing preference", false], ["timeline", "Delivery timeline", false],
];

export function BusinessCollectionPage() {
  const { id } = useParams();
  const { addCollection } = useCart();
  const [col, setCol] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get(`/business/collections/${id}`).then(r => setCol(r.data)).catch(() => setCol(false)); }, [id]);

  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/business/inquiries", { ...f, collection_id: id });
      toast.success("Inquiry sent — Jason will reply within one business day."); setShowForm(false); setF({ message: "" });
    } catch (err) { console.error(err); toast.error("Could not send inquiry"); } finally { setSending(false); }
  };

  if (col === null) return <PublicShell><div className="pt-40 text-center text-mute">Loading…</div></PublicShell>;
  if (col === false) return <PublicShell><div className="pt-40 text-center text-mute">Collection not found.</div></PublicShell>;
  return (
    <PublicShell>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-24" data-testid="business-collection-page">
        <Link to="/business" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-mute hover:text-ink mb-8"><ArrowLeft size={12}/> All collections</Link>
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          <FitImage src={fileUrl(col.cover_image_url)} alt={col.name} className="relative aspect-[4/3] bg-cream-soft" sizes="(min-width:1024px) 55vw, 100vw" />
          <div>
            <div className="overline text-mute mb-3">Business Collection</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink">{col.name}</h1>
            <p className="text-ink-soft leading-relaxed mt-5">{col.description}</p>
            <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-mute">{col.artworks.length} pieces · {col.print_size} in · {col.material} prints</div>
            <div className="mt-2 font-mono-brand text-3xl text-ink" data-testid="collection-price">{formatUSD(col.price_usd)} <span className="text-sm text-mute">flat price, full set</span></div>
            {(col.volume_tiers || []).length > 0 && (
              <div className="mt-4 border-l-2 border-accent pl-4" data-testid="collection-volume-tiers">
                <div className="overline text-mute mb-1">Multiple locations?</div>
                {col.volume_tiers.map(t => (
                  <div key={t.min_qty} className="text-sm text-ink-soft">{t.min_qty}+ sets · <span className="text-ink">{t.percent_off}% off</span> → {formatUSD(col.price_usd * (1 - t.percent_off / 100))} per set</div>
                ))}
                <div className="text-xs text-mute mt-1">Set the quantity in your cart to the number of locations — the discount applies automatically.</div>
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => { addCollection(col); toast.success(`${col.name} added to cart`); }} data-testid="collection-add-to-cart"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-ink text-cream text-[11px] uppercase tracking-[0.18em] hover:bg-ink/85"><ShoppingBag size={14}/> Add to Cart</button>
              <button onClick={() => setShowForm(s => !s)} data-testid="collection-inquire"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-ink text-[11px] uppercase tracking-[0.18em] hover:bg-ink hover:text-cream"><Mail size={14}/> Inquire</button>
            </div>
            {showForm && (
              <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-3 p-6 bg-cream-soft border border-line" data-testid="inquiry-form">
                {FIELDS.map(([k, label, req]) => (
                  <input key={k} required={req} placeholder={label + (req ? " *" : "")} value={f[k] || ""} onChange={e => setF({ ...f, [k]: e.target.value })}
                    data-testid={`inquiry-${k}`} className="bg-white border border-line px-3 py-2.5 text-sm" type={k === "email" ? "email" : "text"} />
                ))}
                <textarea placeholder="Anything else — custom sizes, mixed sets, installation help…" value={f.message} onChange={e => setF({ ...f, message: e.target.value })}
                  data-testid="inquiry-message" rows={4} className="sm:col-span-2 bg-white border border-line px-3 py-2.5 text-sm" />
                <button disabled={sending} data-testid="inquiry-submit" className="sm:col-span-2 px-6 py-3.5 bg-ink text-cream text-[11px] uppercase tracking-[0.18em] disabled:opacity-50">{sending ? "Sending…" : "Send Inquiry"}</button>
              </form>
            )}
          </div>
        </div>
        <h2 className="font-serif text-3xl text-ink mt-20 mb-8">What's included</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="collection-artworks">
          {col.artworks.map(a => (
            <Link key={a.id} to={`/art/${a.id}`} className="block group">
              <FitImage src={fileUrl(a.image_urls?.[0])} alt={a.title} className="relative aspect-[4/5] bg-cream-soft" sizes="25vw" imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="mt-2 font-serif text-lg text-ink">{a.title}</div>
            </Link>
          ))}
        </div>
      </section>
    </PublicShell>
  );
}
